import {
  MenuItem,
  Select,
  styled,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@material-ui/core';
import React, { ReactElement } from 'react';

import { EBool } from '~/src/dash/api/types/bool';
import { BetaTooltipIcon } from '~/src/dash/components/icons/beta-icon';
import { Col, Cols, Row, Rows, Text } from '~/src/dash/publicaUI';

const buildOptions = (values: string[], label?: string) => {
  return values.map((v) => {
    let text = v;
    if (label) {
      text += ' ' + label;
    }
    return {
      value: v,
      text,
    };
  });
};

const VideoResolutions = ['1920x1080', '1280x720', '640x480', '480x360'];
const VideoBitrates = ['15000', '12000', '7500', '6000', '4000', '3500', '2500', '1500'];
const VideoFormats = ['mp4'];
const FrameRates = ['23.98', '24', '25', '29.97', '30'];
const AudioBitrates = ['192', '128', '320'];
const SamplingFrequencies = ['48', '44.1', '96', '88.2'];

const VideoResolutionOptions = buildOptions(VideoResolutions);
const VideoBitrateOptions = buildOptions(VideoBitrates, 'kbps');
const VideoFormatOptions = buildOptions(VideoFormats);
const FrameRateOptions = buildOptions(FrameRates, 'fps');
const CodecOptions = [
  {
    value: 'h.264_high',
    text: 'H.264 High',
  },
  {
    value: 'h.264_main',
    text: 'H.264 Main',
  },
  {
    value: 'h.264_baseline',
    text: 'H.264 Baseline',
  },
];
const AudioBitrateOptions = buildOptions(AudioBitrates, 'kbps');
const SamplingFrequencyOptions = buildOptions(SamplingFrequencies, 'kHz');

const StyledTableCell = styled(TableCell)({
  padding: '0 0 0 12px',
});

interface ICreativeQualityAssuranceView {
  enable: EBool;
  toggleEnable: () => void;
  videoResolution: string;
  handleChangeValue: (name: string, value: string) => void;
  videoBitrate: string;
  videoFormat: string;
  frameRate: string;
  codec: string;
  audioBitrate: string;
  samplingFrequency: string;
}

export const CreativeQualityAssuranceView = ({
  enable,
  videoResolution,
  videoBitrate,
  videoFormat,
  frameRate,
  codec,
  audioBitrate,
  samplingFrequency,
  toggleEnable,
  handleChangeValue,
}: ICreativeQualityAssuranceView): ReactElement => {
  const cqaEnabled = enable === EBool.EBool_TRUE;
  if (!videoResolution) {
    videoResolution = VideoResolutionOptions[0].value;
    handleChangeValue('CreativeQualityAssuranceVideoResolution', videoResolution);
  }
  if (!videoBitrate) {
    videoBitrate = VideoBitrateOptions[0].value;
    handleChangeValue('CreativeQualityAssuranceVideoBitrate', videoBitrate);
  }
  if (!videoFormat) {
    videoFormat = VideoFormatOptions[0].value;
    handleChangeValue('CreativeQualityAssuranceVideoFormat', videoFormat);
  }
  if (!frameRate) {
    frameRate = FrameRateOptions[0].value;
    handleChangeValue('CreativeQualityAssuranceFrameRate', frameRate);
  }
  if (!codec) {
    codec = CodecOptions[0].value;
    handleChangeValue('CreativeQualityAssuranceCodec', codec);
  }
  if (!audioBitrate) {
    audioBitrate = AudioBitrateOptions[0].value;
    handleChangeValue('CreativeQualityAssuranceAudioBitrate', audioBitrate);
  }
  if (!samplingFrequency) {
    samplingFrequency = SamplingFrequencyOptions[0].value;
    handleChangeValue('CreativeQualityAssuranceSamplingFrequency', samplingFrequency);
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleChangeValue(event.target.name, event.target.value);
  };

  return (
    <>
      <Rows noPadding>
        <Row noPadding paddingBottom='16px'>
          <Cols style={{ alignItems: 'center' }}>
            <Col>
              <Text title bold>
                Creative Quality Assurance
              </Text>
            </Col>
            <Col style={{ marginLeft: '10px' }}>
              <BetaTooltipIcon />
            </Col>
          </Cols>
          <Text extraSmall>
            This feature automates the validation and adjustment of creatives to meet specific
            technical requirements.
          </Text>
          <Cols>
            <Switch checked={cqaEnabled} onChange={toggleEnable} />
            <Text small style={{ display: 'flex', alignItems: 'center' }}>
              {cqaEnabled ? 'Active' : 'Inactive'}
            </Text>
          </Cols>
        </Row>

        {cqaEnabled && (
          <TableContainer>
            <Table>
              <TableBody>
                <TableRow>
                  <StyledTableCell>Minimum Video Resolution</StyledTableCell>
                  <StyledTableCell>
                    <Select
                      name='CreativeQualityAssuranceVideoResolution'
                      value={videoResolution}
                      onChange={handleChange}
                      fullWidth
                    >
                      {VideoResolutionOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.text}
                        </MenuItem>
                      ))}
                    </Select>
                  </StyledTableCell>
                </TableRow>
                <TableRow>
                  <StyledTableCell>Minimum Video Bitrate</StyledTableCell>
                  <StyledTableCell>
                    <Select
                      name='CreativeQualityAssuranceVideoBitrate'
                      value={videoBitrate}
                      onChange={handleChange}
                      fullWidth
                    >
                      {VideoBitrateOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.text}
                        </MenuItem>
                      ))}
                    </Select>
                  </StyledTableCell>
                </TableRow>
                <TableRow>
                  <StyledTableCell>Video Format</StyledTableCell>
                  <StyledTableCell>
                    <Select
                      name='CreativeQualityAssuranceVideoFormat'
                      value={videoFormat}
                      onChange={handleChange}
                      fullWidth
                    >
                      {VideoFormatOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.text}
                        </MenuItem>
                      ))}
                    </Select>
                  </StyledTableCell>
                </TableRow>
                <TableRow>
                  <StyledTableCell>Minimum Frame Rate</StyledTableCell>
                  <StyledTableCell>
                    <Select
                      name='CreativeQualityAssuranceFrameRate'
                      value={frameRate}
                      onChange={handleChange}
                      fullWidth
                    >
                      {FrameRateOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.text}
                        </MenuItem>
                      ))}
                    </Select>
                  </StyledTableCell>
                </TableRow>
                <TableRow>
                  <StyledTableCell>Codec</StyledTableCell>
                  <StyledTableCell>
                    <Select
                      name='CreativeQualityAssuranceCodec'
                      value={codec}
                      onChange={handleChange}
                      fullWidth
                    >
                      {CodecOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.text}
                        </MenuItem>
                      ))}
                    </Select>
                  </StyledTableCell>
                </TableRow>
                <TableRow>
                  <StyledTableCell>Minimum Audio Bitrate</StyledTableCell>
                  <StyledTableCell>
                    <Select
                      name='CreativeQualityAssuranceAudioBitrate'
                      value={audioBitrate}
                      onChange={handleChange}
                      fullWidth
                    >
                      {AudioBitrateOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.text}
                        </MenuItem>
                      ))}
                    </Select>
                  </StyledTableCell>
                </TableRow>
                <TableRow>
                  <StyledTableCell>Minimum Sampling Frequency</StyledTableCell>
                  <StyledTableCell>
                    <Select
                      name='CreativeQualityAssuranceSamplingFrequency'
                      value={samplingFrequency}
                      onChange={handleChange}
                      fullWidth
                    >
                      {SamplingFrequencyOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.text}
                        </MenuItem>
                      ))}
                    </Select>
                  </StyledTableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Rows>
    </>
  );
};
